import { useContractRead, useContractReads } from 'wagmi';
import { useMemo } from 'react';
import { CONFIG, ADDRESSES } from '../config';
import { Abi, formatUnits } from 'viem';
import { useEntityDetails } from './useEntityDetails';
import { useAccount } from 'wagmi';

export type TokenAddress = `0x${string}`;

export interface AssetData {
  supportedAssets: TokenAddress[];
  maxWalletAmounts: Record<TokenAddress, bigint>;
  balances: Record<TokenAddress, bigint>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useAssetData(productAddress?: `0x${string}`): AssetData {
  const { entities } = useEntityDetails();
  const { address: userAddress } = useAccount();

  // Get product type from entities first
  const productType = useMemo(() => {
    if (productAddress) {
      for (const entity of entities) {
        const product = entity.products.find(p => p.address === productAddress);
        if (product) {
          return product.className;
        }
      }
    }
    return undefined;
  }, [productAddress, entities]);

  // Get supported assets based on product type
  const { data: rawSupportedAssets, isLoading: isLoadingAssets, error: assetsError, refetch: refetchAssets } = useContractRead({
    address: productAddress,
    abi: (productType === 'TrinityBond' ? CONFIG.TRINITY_BOND_ABI.abi : CONFIG.TRINITY_PRODUCT_ABI.abi) as Abi,
    functionName: 'getSupportedAssets',
    enabled: !!productAddress,
  });

  // Type-safe supported assets
  const supportedAssetsData = useMemo(() => {
    if (Array.isArray(rawSupportedAssets)) {
      return rawSupportedAssets as TokenAddress[];
    }
    return [] as TokenAddress[];
  }, [rawSupportedAssets]);

  // Return empty data for non-bond products
  if (productType && productType !== 'TrinityBond') {
    const emptyAmounts = Object.fromEntries(
      Object.entries(ADDRESSES).map(([_, addr]) => [addr as TokenAddress, ''])
    ) as Record<TokenAddress, string>;
    
    const emptyBalances = Object.fromEntries(
      Object.entries(ADDRESSES).map(([_, addr]) => [addr as TokenAddress, 0n])
    ) as Record<TokenAddress, bigint>;
    
    return {
      supportedAssets: [],
      maxWalletAmounts: emptyAmounts,
      balances: emptyBalances,
      isLoading: false,
      error: null,
      refetch: async () => {}
    };
  }

  // Get current max per wallet values for TrinityBond only
  const { data: maxPerWallets = [], isLoading: isLoadingMaxWallets, error: maxWalletsError, refetch: refetchMaxWallets } = useContractReads({
    contracts: supportedAssetsData.map((asset: TokenAddress) => ({
      address: productAddress || '0x0000000000000000000000000000000000000000',
      abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
      functionName: 'maxPerWallet',
      args: [asset],
    })),
    enabled: !!productAddress && productType === 'TrinityBond' && supportedAssetsData.length > 0,
  });

  // Get balances for supported assets from their token contracts
  const { data: balanceData = [], isLoading: isLoadingBalances, error: balancesError, refetch: refetchBalances } = useContractReads({
    contracts: supportedAssetsData.map((asset: TokenAddress) => ({
      address: asset,
      abi: CONFIG.ERC20_ABI.abi as Abi,
      functionName: 'balanceOf',
      args: [productAddress || '0x0000000000000000000000000000000000000000'],
    })),
    enabled: !!productAddress && supportedAssetsData.length > 0,
  });

  // Memoize initial max wallet amounts
  const initialMaxWalletAmounts = useMemo(() => 
    Object.fromEntries(
      Object.entries(ADDRESSES).map(([_, addr]) => [addr as TokenAddress, 0n])
    ) as Record<TokenAddress, bigint>,
    []
  );

  // Memoize initial balances
  const initialBalances = useMemo(() => 
    Object.fromEntries(
      Object.entries(ADDRESSES).map(([_, addr]) => [addr as TokenAddress, 0n])
    ) as Record<TokenAddress, bigint>,
    []
  );

  // Memoize max wallet amounts calculation
  const maxWalletAmounts = useMemo(() => {
    if (supportedAssetsData.length > 0 && maxPerWallets.length > 0) {
      const newAmounts = { ...initialMaxWalletAmounts };
      supportedAssetsData.forEach((asset: TokenAddress, index: number) => {
        if (maxPerWallets[index]?.result) {
          newAmounts[asset] = maxPerWallets[index].result as bigint;
        }
      });
      return newAmounts;
    }
    return initialMaxWalletAmounts;
  }, [supportedAssetsData, maxPerWallets, initialMaxWalletAmounts]);

  // Memoize balances calculation
  const balances = useMemo(() => {
    if (supportedAssetsData.length > 0 && balanceData.length > 0) {
      const newBalances = { ...initialBalances };
      supportedAssetsData.forEach((asset: TokenAddress, index: number) => {
        if (balanceData[index]?.result) {
          newBalances[asset] = balanceData[index].result as bigint;
        }
      });
      return newBalances;
    }
    return initialBalances;
  }, [supportedAssetsData, balanceData, initialBalances]);

  // Combined refetch function
  const refetch = async () => {
    await Promise.all([
      refetchAssets(),
      refetchMaxWallets(),
      refetchBalances()
    ]);
  };

  return {
    supportedAssets: supportedAssetsData,
    maxWalletAmounts,
    balances,
    isLoading: isLoadingAssets || isLoadingMaxWallets || isLoadingBalances,
    error: assetsError || maxWalletsError || balancesError,
    refetch
  };
} 